import type { OrderPriority } from "@/features/orders/orders.api";
import { apiClient } from "@/lib/api";
import type { RepeatResult } from "@/features/orders/orders.api";
import type { MailLists } from "@/features/orders/orderListTemplates.api";
import type { FieldMap, ListRowInput } from "@/features/orders/listRows";

// The invoerbak (docs/invoerbak.md, docs/api.md §9.62): what order entry and planning still have to enter, the row of a
// mail with its kept copy, the proposal the model makes on a click, and what was made of it.

export type IntakeSource = "manual" | "folder" | "sender";
export type WaitingFor = "customer" | "planning";
export type NoOrderReason = "question" | "quote" | "duplicate" | "status" | "other";

export interface IntakeRow {
  id: string;
  source: IntakeSource;
  subject: string;
  from: string | null;
  fromName: string | null;
  receivedAt: string | null;
  party: { id: string; code: string; name: string } | null;
  templateId: string | null;
  addedBy: string | null;
  mailboxes: string[];
  status: "open" | "waiting" | "done";
  waitingFor: WaitingFor | null;
  waitingNote: string | null;
  waitingUntil: string | null;
  urgent: boolean;
  dueDate: string | null;
  claimedBy: { id: string; name: string | null; me: boolean } | null;
  hasProposal: boolean;
  made: { kind: "order" | "warehouse_order"; id: string; number: string }[];
  attachments: number;
  createdAt: string;
}

export interface AttentionRow {
  id: string;
  number: string;
  customerReference: string | null;
  receivedAt: string;
  reasons: string[];
  party: { id: string; code: string; name: string };
}

export interface Bak {
  items: IntakeRow[];
  attention: AttentionRow[];
}

export interface Field<T> {
  value: T | null;
  evidence: string | null;
  /** standard: not in the mail, from the ordermail's standard order (25-10-2026). */
  state: "found" | "not_found" | "missing" | "standard" | "stock";
}

export interface ProposedStop {
  date: Field<string>;
  from: Field<string>;
  to: Field<string>;
  reference: Field<string>;
  instruction: Field<string>;
  place: { name: Field<string>; street: Field<string>; postalCode: Field<string>; city: Field<string>; countryCode: Field<string> };
  locationId: string | null;
  locationName: string | null;
  warehouse: { code: string; name: string } | null;
  /** The place is the ordermail's standard one: the mail named none. */
  placeFromStandard?: boolean;
  /** The place is the warehouse that holds the customer's stock: the goods come from stock and the mail named none. */
  placeFromStock?: boolean;
  /** What the mail asks of the lorry at this stop (*Leveren met laadklep*: tail_lift), with its words. */
  requirements?: { value: string; evidence: string }[];
}

export interface ProposedOrder {
  customerReference: Field<string>;
  load: ProposedStop;
  unload: ProposedStop;
  pallets: Field<number>;
  loadingMeters: Field<number>;
  weightKg: Field<number>;
  description: Field<string>;
  remark: Field<string>;
  temperatureMinC: Field<number>;
  temperatureMaxC: Field<number>;
  warehouseOrder: {
    kind: "outbound" | "inbound";
    /** null: from stock, in a warehouse WLX does not know — chosen on the warehouse order. */
    warehouse: { code: string; name: string } | null;
    lines: { article: Field<string>; quantity: Field<number>; unit: Field<string> }[];
  } | null;
  duplicate: { kind: "order" | "warehouse_order" | "wms"; number: string } | null;
  /** The pdf of the mail that travels with the goods as packing slip, printed behind the CMR. */
  packingSlip?: { attachmentId: string; name: string } | null;
}

/** The sender in box 1 of the CMR, as a consignment keeps a party. */
export interface ProposalShipper {
  name: string;
  street: string | null;
  houseNumber: string | null;
  houseNumberAddition: string | null;
  postalCode: string | null;
  city: string | null;
  countryCode: string | null;
}

/** What the ordermail's standard order adds that the mail does not say. */
export interface ProposalStandard {
  priority: OrderPriority;
  requirementIds: string[];
  /** The sender in box 1 of the CMR the ordermail sets; null: the loading address. */
  shipper?: ProposalShipper | null;
  load: { contactName: string | null; contactPhone: string | null; requirements: string[] };
  unload: { contactName: string | null; contactPhone: string | null; requirements: string[] };
}

export interface Proposal {
  at: string;
  partyId: string | null;
  orders: ProposedOrder[];
  standard?: ProposalStandard | null;
  examples: number;
  note: string | null;
}

export interface MadeEntry {
  kind: "order" | "warehouse_order";
  id: string;
  number: string;
  at: string;
  byId: string;
  undoneAt?: string | null;
  /** The packing slips put with the order: added, there already (added false), or not stored with why. */
  packingSlips?: { name: string; added: boolean; error?: string }[];
}

export interface IntakeItem extends IntakeRow {
  bodyText: string;
  bodyHtml: string | null;
  inboundMessageId: string | null;
  proposal: Proposal | null;
  outcome: "made" | "changed" | "no_order" | null;
  noOrderReason: NoOrderReason | null;
  doneAt: string | null;
  madeFull: MadeEntry[];
  files: { id: string; name: string; contentType: string | null; size: number }[];
  /** Orders of the customer whose number or reference the mail names: what a change is about. */
  about: { id: string; number: string; customerReference: string | null; status: string }[];
}

export const intakeKeys = {
  all: ["intake"] as const,
  bak: ["intake", "bak"] as const,
  item: (id: string) => ["intake", "item", id] as const,
  lists: (id: string) => ["intake", "lists", id] as const,
  samples: (partyId: string) => ["intake", "samples", partyId] as const,
  find: (q: string, partyId: string | null) => ["intake", "find", q, partyId] as const,
  mailboxes: ["intake", "mailboxes"] as const,
};

export const getBak = () => apiClient.get<Bak>("/api/v1/intake");
export const getIntakeItem = (id: string) => apiClient.get<IntakeItem>(`/api/v1/intake/${id}`);
export const addToIntake = (mailboxId: string, messageId: string) =>
  apiClient.post<{ id: string; created: boolean }>("/api/v1/intake/from-mail", { mailboxId, messageId });
export const claimIntake = (id: string) => apiClient.post<{ claimed: boolean }>(`/api/v1/intake/${id}/claim`, {});
export const releaseIntake = (id: string) => apiClient.post<{ released: boolean }>(`/api/v1/intake/${id}/release`, {});
export const setIntakeWaiting = (id: string, body: { waitingFor: WaitingFor | null; note?: string | null; until?: string | null }) =>
  apiClient.post<{ waiting: WaitingFor | null }>(`/api/v1/intake/${id}/waiting`, body);
export const setIntakeUrgent = (id: string, urgent: boolean) => apiClient.post<{ urgent: boolean }>(`/api/v1/intake/${id}/urgent`, { urgent });
export const setIntakeParty = (id: string, partyId: string | null) => apiClient.post<{ partyId: string | null }>(`/api/v1/intake/${id}/party`, { partyId });
export const noOrderIntake = (id: string, reason: NoOrderReason) => apiClient.post<{ done: boolean }>(`/api/v1/intake/${id}/no-order`, { reason });
export const finishIntake = (id: string, outcome: "made" | "changed") => apiClient.post<{ done: boolean }>(`/api/v1/intake/${id}/finish`, { outcome });
export const makeIntakeProposal = (id: string) => apiClient.post<Proposal>(`/api/v1/intake/${id}/proposal`, {});
export const markIntakeMade = (id: string, kind: "order" | "warehouse_order", orderId: string, packingSlips: string[] = []) =>
  apiClient.post<MadeEntry>(`/api/v1/intake/${id}/made`, { kind, id: orderId, ...(packingSlips.length > 0 ? { packingSlips } : {}) });
export const undoIntakeMade = (id: string, kind: "order" | "warehouse_order", orderId: string) =>
  apiClient.post<{ undone: boolean }>(`/api/v1/intake/${id}/undo`, { kind, id: orderId });
export const readIntakeLists = (id: string) => apiClient.get<MailLists>(`/api/v1/intake/${id}/lists`);
/** A mail of the customer in the bak, to set an ordermail up on (relation card, admins). */
export interface IntakeSample {
  id: string;
  subject: string;
  from: string | null;
  fromName: string | null;
  receivedAt: string | null;
  status: string;
  attachments: number;
}
export const listIntakeSamples = (partyId: string) => apiClient.get<IntakeSample[]>(`/api/v1/intake/samples?partyId=${encodeURIComponent(partyId)}`);
export const intakeListOrders = (
  id: string,
  body: { templateId: string; rows: ListRowInput[]; provisional: boolean; noInvoiceReason: string | null; fields?: FieldMap; update?: number[] },
) => apiClient.post<RepeatResult>(`/api/v1/intake/${id}/list-orders`, body);
export const findIntakeOrders = (q: string, partyId: string | null) =>
  apiClient.get<{ id: string; number: string; customerReference: string | null; status: string; party: { code: string; name: string } }[]>(
    `/api/v1/intake/find-orders?q=${encodeURIComponent(q)}${partyId ? `&partyId=${encodeURIComponent(partyId)}` : ""}`,
  );
export const listIntakeMailboxes = () => apiClient.get<{ id: string; email: string; displayName: string; intakeEnabled: boolean }[]>("/api/v1/intake-mailboxes");
export const setIntakeMailbox = (id: string, intakeEnabled: boolean) =>
  apiClient.put<{ id: string; intakeEnabled: boolean }>(`/api/v1/intake-mailboxes/${id}`, { intakeEnabled });
export const attachmentUrl = (id: string, attachmentId: string) => `/api/v1/intake/${id}/attachments/${attachmentId}`;
export const listOrdermailStats = () => apiClient.get<{ templateId: string; total: number; open: number; noOrder: number }[]>("/api/v1/intake-ordermail-stats");
